<div id="maincontent" class="sav-deals-page main">
  <div class="sav-site-container pt-4 lg:pt-6">
    <app-breadcrumb [items]="crumbs" />
  </div>

  <!-- ===== Hero carousel (slides managed in admin: /settings/deals-page/banner) ===== -->
  @if (hasHero()) {
    <section class="pt-4 lg:pt-6" aria-label="Featured deals">
      <div class="sav-site-container">
        <div class="relative overflow-hidden rounded-[16px]">
          <div
            class="flex transition-transform duration-500 ease-out"
            [style.transform]="'translateX(-' + currentHeroSlide() * 100 + '%)'"
          >
            @for (slide of heroSlides(); track slide.imageUrl + $index) {
              @if (slide.isExternal) {
                <a
                  [href]="slide.linkUrl"
                  target="_blank"
                  rel="nofollow sponsored noopener"
                  class="block w-full shrink-0"
                >
                  <img
                    [src]="slide.imageUrl"
                    [alt]="slide.altText"
                    width="1500"
                    height="300"
                    class="block h-auto w-full"
                  />
                </a>
              } @else {
                <a [routerLink]="slide.linkUrl" class="block w-full shrink-0">
                  <img
                    [src]="slide.imageUrl"
                    [alt]="slide.altText"
                    width="1500"
                    height="300"
                    class="block h-auto w-full"
                  />
                </a>
              }
            }
          </div>

          @if (hasHeroCarousel()) {
            <button
              type="button"
              class="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white"
              aria-label="Previous slide"
              (click)="showPreviousHeroSlide()"
            >
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              type="button"
              class="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white"
              aria-label="Next slide"
              (click)="showNextHeroSlide()"
            >
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
            <div class="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2 sm:bottom-4">
              @for (slide of heroSlides(); track slide.imageUrl + $index; let i = $index) {
                <button
                  type="button"
                  class="h-2.5 rounded-full transition-all"
                  [class.w-8]="currentHeroSlide() === i"
                  [class.bg-orange]="currentHeroSlide() === i"
                  [class.w-2.5]="currentHeroSlide() !== i"
                  [class.bg-white]="currentHeroSlide() !== i"
                  [attr.aria-label]="'Go to slide ' + (i + 1)"
                  (click)="selectHeroSlide(i)"
                ></button>
              }
            </div>
          }
        </div>
      </div>
    </section>
  }

  <!-- The page's H1. This page had none at all: its hero is an image carousel with no text, and
       every section below opened at h2, so the outline started at h2 and the page's own topic was
       never stated in a heading. -->
  <section class="pt-6" aria-label="Deals introduction">
    <div class="sav-site-container">
      <header class="sav-section-head">
        <h1>Deals &amp; Product Offers</h1>
        <p>
          Curated discounts across smart home, car tech and everyday essentials — updated daily from
          the stores we partner with.
        </p>
      </header>
    </div>
  </section>

  <!-- ===== Popular Categories (live feeds, same as the home page) ===== -->
  @if (visiblePopularCategories().length > 0) {
    <section class="sav-popular-categories-section" aria-label="Popular categories">
      <div class="sav-popular-categories-wrap">
        <h2>Popular Categories</h2>
        <p>Pick a category to see the deals running in it right now.</p>
        <div class="sav-popular-categories-tabs" role="tablist" aria-label="Category feeds">
          @for (tab of popularCategoryTabs(); track tab.id) {
            <button
              type="button"
              role="tab"
              [class.active]="selectedPopularCategoryTabId() === tab.id"
              [id]="'popular-categories-tab-' + tab.id"
              [attr.aria-selected]="selectedPopularCategoryTabId() === tab.id"
              [attr.aria-controls]="'popular-categories-panel-' + tab.id"
              (click)="selectPopularCategoryTab(tab.id)"
            >
              {{ tab.label }}
            </button>
          }
        </div>
        <div
          class="sav-popular-categories-grid"
          role="tabpanel"
          [id]="'popular-categories-panel-' + selectedPopularCategoryTabId()"
          [attr.aria-labelledby]="'popular-categories-tab-' + selectedPopularCategoryTabId()"
        >
          @for (category of visiblePopularCategories(); track category.label + $index) {
            <a
              [class]="'sav-popular-category-card ' + (category.thumb || '')"
              [routerLink]="category.path || null"
            >
              <span class="sav-popular-category-thumb" aria-hidden="true">
                @if (category.imageUrl) {
                  <img [src]="category.imageUrl" [alt]="category.label" loading="lazy" />
                } @else if (category.iconHtml) {
                  <span class="sav-popular-category-icon" [innerHTML]="category.iconHtml"></span>
                }
              </span>
              <span class="sav-popular-category-label">{{ category.label }}</span>
            </a>
          }
        </div>
        <div class="sav-popular-categories-dots" aria-hidden="true">
          @for (index of popularCategorySlideIndexes(); track index) {
            <button
              type="button"
              [class.active]="currentPopularCategorySlide() === index"
              [attr.aria-label]="'Go to category slide ' + (index + 1)"
              (click)="selectPopularCategorySlide(index)"
            ></button>
          }
        </div>
      </div>
    </section>
  }

  <!-- ===== Shop Our Top Picks (live data — biggest discount per store) ===== -->
  @if (topPicks().length > 0) {
    <section class="pb-10" aria-label="Top deals">
      <div class="sav-site-container">
        <header class="sav-section-head">
          <h2>{{ topPicksHeading() || 'Shop Our Top Picks' }}</h2>
          <p>The biggest price drops across our stores right now</p>
        </header>
        <div class="sav-product-grid sav-full-rows">
          @for (p of topPicks(); track p.href) {
            <app-product-card
              [title]="p.title"
              [image]="p.image"
              [hoverImage]="p.hoverImage"
              [storeName]="p.storeName"
              [storeLogoUrl]="p.storeLogoUrl"
              [price]="p.price"
              [oldPrice]="p.oldPrice"
              [href]="p.href"
            />
          }
        </div>
      </div>
    </section>
  }

  <!-- ===== New Arrivals (live data — no static dot) ===== -->
  @if (newArrivals().length > 0) {
    <section class="pb-10" aria-label="New arrivals">
      <div class="sav-site-container">
        <header class="sav-section-head">
          <h2>New Arrivals</h2>
          <p>The latest products added across our stores</p>
        </header>
        <div class="sav-product-grid sav-full-rows">
          @for (p of newArrivals(); track p.href) {
            <app-product-card
              [title]="p.title"
              [image]="p.image"
              [hoverImage]="p.hoverImage"
              [storeName]="p.storeName"
              [storeLogoUrl]="p.storeLogoUrl"
              [price]="p.price"
              [oldPrice]="p.oldPrice"
              [href]="p.href"
            />
          }
        </div>
      </div>
    </section>
  }

  <!-- ===== Trust marquee ===== -->
  <section class="relative overflow-hidden bg-orange py-2.5 text-white">
    <app-dev-flag position="corner" note="Hardcoded trust labels — not admin-managed" />
    <div
      class="sav-trust-marquee flex w-max whitespace-nowrap text-sm font-bold uppercase tracking-[0.14em]"
    >
      @for (item of trustItems; track item) {
        <span class="mx-8">{{ item }}</span>
      }
      @for (item of trustItems; track 'dup' + item) {
        <span class="mx-8">{{ item }}</span>
      }
    </div>
  </section>

  <!-- ===== Top Stores carousel (live data — stores ranked by their deepest markdown) =====
       Absent when nothing in the catalogue is discounted. It used to fall back to a hardcoded row
       of logos so it always rendered, but a heading reading "the biggest discounts right now" over
       placeholder brands that mostly linked to /storelist promised something the page did not
       have. -->
  @if (topDiscountStores().length > 0) {
    <section class="py-10" aria-label="Event top stores">
      <div class="sav-site-container">
        <header class="sav-section-head">
          <h2>{{ topStoresHeading }}</h2>
          <p>Stores running the biggest discounts right now</p>
        </header>

        <div class="relative mt-8">
          <button
            type="button"
            class="absolute top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white left-0"
            aria-label="Previous stores"
            (click)="scrollTopStores(-1)"
          >
            <svg
              class="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>

          <div
            #topStoresTrack
            class="sav-scrollable flex gap-7 overflow-x-auto scroll-smooth px-12 py-2 xl:gap-10"
          >
            @for (store of topDiscountStores(); track store.path) {
              <a
                [routerLink]="store.path"
                [attr.aria-label]="store.name"
                class="group grid h-[120px] w-[120px] shrink-0 place-items-center overflow-hidden rounded-full bg-white shadow-[0_6px_16px_rgba(16,26,40,0.12)] xl:h-[138px] xl:w-[138px]"
              >
                <!--
                object-contain, not object-cover: these are real merchant logos, mostly wide
                wordmarks. Cover fills the circle by cropping, which sliced "Spartoo" down to "art".
                Padding is deliberately tight. The logos are 168x56 (3:1), and a 3:1 mark still fits
                inside a circle at ~95% of its diameter, so a wide inset only strands white space and
                leaves the wordmark looking lost in the circle.
              -->
                <img
                  [src]="store.logoUrl"
                  [alt]="store.name"
                  loading="lazy"
                  class="h-full w-full rounded-full object-contain p-2 transition duration-300 group-hover:scale-110"
                />
              </a>
            }
          </div>

          <button
            type="button"
            class="absolute top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white right-0"
            aria-label="Next stores"
            (click)="scrollTopStores(1)"
          >
            <svg
              class="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  }

  <!-- ===== Featured store heroes (admin toggle per deals store; carousel when >1) ===== -->
  @if (featuredHeroes().length > 0) {
    <section class="pb-10" aria-label="Featured stores">
      <div class="sav-site-container">
        <header class="sav-section-head">
          <h2>Brands in the Spotlight</h2>
          <p>Official stores with offers running right now</p>
        </header>
        <div class="relative">
          @if (featuredHeroes().length > 1) {
            <button
              type="button"
              class="absolute left-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white"
              aria-label="Previous store"
              (click)="scrollFeaturedHeroes(-1)"
            >
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
          }

          <div
            #featuredHeroTrack
            class="sav-scrollable flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth"
          >
            @for (hero of featuredHeroes(); track hero.path) {
              <article
                class="w-full shrink-0 snap-start overflow-hidden rounded-[24px] bg-[#f8f3fb] shadow-[0_18px_45px_-30px_rgba(15,23,42,0.24)]"
              >
                <div class="relative">
                  @if (hero.bannerUrl) {
                    <img
                      [src]="hero.bannerUrl"
                      [alt]="hero.name + ' banner'"
                      width="1500"
                      height="300"
                      class="block aspect-[1500/300] w-full object-cover"
                    />
                  } @else {
                    <div class="aspect-[1500/300] w-full"></div>
                  }

                  <div class="absolute inset-x-6 bottom-6 flex items-end justify-between gap-5">
                    @if (hero.logoUrl) {
                      <img
                        [src]="hero.logoUrl"
                        [alt]="hero.name + ' logo'"
                        width="120"
                        height="120"
                        class="h-[92px] w-[92px] shrink-0 rounded-full bg-white object-contain p-2 shadow-[0_10px_24px_rgba(15,23,42,0.14)] xl:h-[120px] xl:w-[120px]"
                      />
                    }

                    <div class="flex flex-wrap items-center justify-end gap-3">
                      @if (hero.affiliateLink) {
                        <a
                          [href]="hero.affiliateLink"
                          target="_blank"
                          rel="nofollow sponsored noopener"
                          class="inline-flex min-h-[44px] items-center rounded-full bg-white/95 px-5 text-[14px] font-bold text-orange shadow-soft backdrop-blur transition hover:-translate-y-0.5"
                          >{{ hero.buttonText }}</a
                        >
                      }
                      <!-- Following is an email signup, and that form lives on the store's own
                           page — so this sends the visitor there rather than faking a one-click
                           follow. -->
                      <a
                        [routerLink]="'/' + hero.slug"
                        class="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-orange px-5 text-[14px] font-bold text-white transition hover:-translate-y-0.5"
                        >Follow<span class="text-[12px] opacity-80">{{
                          formatCount(hero.followers)
                        }}</span></a
                      >
                      <button
                        type="button"
                        class="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white/95 px-5 text-[14px] font-bold text-orange shadow-soft backdrop-blur transition hover:-translate-y-0.5"
                        (click)="shareHero(hero)"
                      >
                        {{ hero.shareLabel
                        }}<span class="text-[12px] opacity-80">{{ formatCount(hero.shares) }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            }
          </div>

          @if (featuredHeroes().length > 1) {
            <button
              type="button"
              class="absolute right-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-orange bg-white/95 text-orange shadow-soft transition hover:bg-orange hover:text-white"
              aria-label="Next store"
              (click)="scrollFeaturedHeroes(1)"
            >
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          }
        </div>
      </div>
    </section>
  }

  <!-- ===== Category Products (Settings » Deals Page » Category Products) ===== -->
  @for (section of categoryProducts(); track section.categoryPath) {
    <section class="py-8 md:py-10" aria-label="Category products">
      <div class="sav-site-container">
        <header class="sav-section-head">
          <h2>{{ section.categoryName }}</h2>
          <p>The best {{ section.categoryName }} deals running right now</p>
        </header>
        <div class="sav-product-grid sav-full-rows">
          @for (p of section.products; track p.href) {
            <app-product-card
              [title]="p.title"
              [image]="p.image"
              [hoverImage]="p.hoverImage"
              [storeName]="p.storeName"
              [storeLogoUrl]="p.storeLogoUrl"
              [price]="p.price"
              [oldPrice]="p.oldPrice"
              [href]="p.href"
            />
          }
        </div>
        <div class="mt-6 flex justify-center">
          <a class="sav-cross-page-link" [routerLink]="section.categoryPath">
            See all {{ section.categoryName }} products
          </a>
        </div>
      </div>
    </section>
  }

  <!-- ===== Top Picks for you — same markup/styles as the home page (static data) ===== -->
  <!--
    Stores attached to the currently featured event that have products. Uses the dashed store-tile
    design from the category pages; those classes live in the global styles.scss, so no local styles
    are needed here. Hidden entirely when the featured event has no such stores.
  -->
  @if (eventStoreCards().length > 0) {
    <section class="sav-trending-stores-section" aria-label="Featured event stores">
      <div class="sav-trending-stores-wrap">
        <h2>Top Picks for you</h2>
        @if (featuredEventName()) {
          <p>Stores featured in {{ featuredEventName() }}</p>
        }

        <div class="sav-trending-stores-grid">
          @for (brand of eventStoreCards(); track brand.href) {
            <a
              class="sav-trending-store-card"
              [routerLink]="brand.href"
            >
              <span class="sav-store-tile-logo-box">
                <!-- The caption below names the store, so the artwork is decorative. -->
                <app-store-logo [logoUrl]="brand.logoUrl" [name]="brand.name" decorative />
              </span>
              <span class="sav-store-tile-divider" aria-hidden="true"></span>
              <span class="sav-store-tile-store-meta">
                <span class="sav-store-tile-brand-name">{{ brand.name }}</span>
                @if (brand.couponsCount > 0) {
                  <span class="sav-store-tile-count"
                    >{{ brand.couponsCount }}+ Coupons &amp; Deals</span
                  >
                }
              </span>
            </a>
          }
        </div>
      </div>
    </section>
  }

  <!-- ===== Big sale newsletter banner — same section as the home page (static) ===== -->
  <!-- Shared banner (see home page): both promo links follow the featured event. -->
  <app-big-sale-banner />

  <!-- ===== Blogs — same card design as /blogs/:categorySlug, live posts ===== -->
  @if (blogPosts().length > 0) {
    <section class="px-0 py-[50px]" aria-label="Latest guides">
      <div class="sav-site-container">
        <header class="sav-section-head">
          <h2>Latest Guides &amp; Insights</h2>
          <p>How to time a purchase, stack an offer and avoid the common traps.</p>
        </header>
        <div class="grid gap-10 md:grid-cols-2 xl:grid-cols-3">
          @for (post of blogPosts(); track post.slug) {
            <article class="group cursor-pointer">
              <a [routerLink]="['/blogs', post.slug]" class="block">
                <div class="relative mb-5 aspect-[5/4] overflow-hidden rounded-[2.5rem] shadow-sm">
                  <span
                    class="absolute left-4 top-4 z-10 inline-flex rounded-full bg-[color-mix(in_srgb,var(--color-primary)_12%,white)] px-[14px] py-[6px] text-[10px] font-bold leading-none uppercase tracking-[0.18em] text-[var(--color-primary)]"
                  >
                    {{ post.category }}
                  </span>
                  <img
                    [src]="post.imageUrl"
                    [alt]="post.title"
                    class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  @if (post.storeLogoUrl) {
                    <span
                      class="absolute bottom-4 right-4 w-14 h-14 rounded-full bg-white shadow-lg overflow-hidden ring-1 ring-black/5"
                    >
                      <img
                        [src]="post.storeLogoUrl"
                        alt="Store logo"
                        class="w-full h-full object-cover rounded-full"
                      />
                    </span>
                  }
                </div>
              </a>

              <div
                class="mb-3 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[var(--color-primary)] font-black"
              >
                <span class="text-slate-400">{{ post.readTime }}</span>
                <span class="h-1 w-1 rounded-full bg-slate-300"></span>
                @if (post.authorPath) {
                  <a
                    class="text-slate-500 transition-colors hover:text-[var(--color-primary-deep)]"
                    [routerLink]="post.authorPath"
                  >
                    {{ post.author }}
                  </a>
                } @else {
                  <span class="text-slate-500">{{ post.author }}</span>
                }
              </div>

              <h3 class="mb-3 transition-colors group-hover:text-[var(--color-primary)]">
                <a [routerLink]="['/blogs', post.slug]">{{ truncateText(post.title, 60) }}</a>
              </h3>

              <p class="text-[14px] leading-[1.6] text-slate-500 font-medium">
                {{ post.excerpt }}
              </p>
            </article>
          }
        </div>
      </div>
    </section>
  }
</div>
